import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DocumentSchema, SchemaField, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { FilterGroup, Filter, FilterGroupType, RuleOperator } from 'src/app/modules/core/store/models/filters.model';
import { Workflow } from 'src/app/modules/core/store/models/workflow.model';
import { Store } from '@ngrx/store';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { first, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { FilterDisplayBuilder } from './filter-display-builder.service';
import { LinkFilterGroup } from '../../../store/models/link-filters.model';
import { Dataset } from '../../../store/models/dataset.model';

@Component({
    selector: 'topo-link-filter-display',
    templateUrl: './filter-display.component.html',
    styleUrls: ['./filter-display.component.scss'],
    standalone: false
})
export class LinkFilterDisplayComponent implements OnInit, OnDestroy {
  public destroy$ = new Subject<void>();
  public _filterGroup: LinkFilterGroup;
  private _filterGroupValueBeforeEdit: LinkFilterGroup;
  @Input()
  public get filterGroup(): LinkFilterGroup {
    return this._filterGroup;
  }
  public set filterGroup(value: LinkFilterGroup) {
    this._filterGroup = value;
    this.buildFilterDisplayText();
  }
  @Input() maxLength: number = 0;
  @Input() datasets: Dataset[];
  private _actionIfDescription: string;
  @Input()
  public set actionIfDescription(value: string) {
    this.translate
      .get(value)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(v => {
        this._actionIfDescription = v;
        this.buildFilterDisplayText();
      });
  }
  private _actionResultDescription: string;
  @Input()
  public set actionResultDescription(value: string) {
    this.translate
      .get(value)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(v => {
        this._actionResultDescription = v;
        this.buildFilterDisplayText();
      });
  }
  @Input() showInstructions = true;
  @Input() lineClamp: number;
  private _disabled: boolean;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
  }
  get disabled() {
    return this._disabled;
  }
  @Input() lookupTargetSchemaFields: SchemaField[];
  @Input() readOnly: boolean;

  @Output() filterChanged: EventEmitter<LinkFilterGroup> = new EventEmitter<LinkFilterGroup>();
  showFilterDialog: boolean;
  filterDisplayText: string;
  showNewFilterButton: boolean;
  operatorDisplayValues: any;
  unitsConversionValue: number;
  distanceName: string;
  andText: string;
  orText: string;
  dateFilterValues: any;

  constructor(private store: Store<fromUserProfile.State>, private translate: TranslateService) {}

  ngOnInit(): void {
    this.operatorDisplayValues = {
      [RuleOperator.Equal]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Equal}`),
      [RuleOperator.NotEqual]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotEqual}`),
      [RuleOperator.GreaterThan]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.GreaterThan}`),
      [RuleOperator.GreaterThanEqualTo]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.GreaterThanEqualTo}`),
      [RuleOperator.LessThan]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.LessThan}`),
      [RuleOperator.LessThanEqualTo]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.LessThanEqualTo}`),
      [RuleOperator.Contains]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Contains}`),
      [RuleOperator.Proximity]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Proximity}`),
      [RuleOperator.DateIsWithinLast]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsWithinLast}`),
      [RuleOperator.DoesNotContain]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DoesNotContain}`),
      [RuleOperator.DateIsNotWithinLast]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsNotWithinLast}`),
      [RuleOperator.IsEmpty]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.IsEmpty}`),
      [RuleOperator.IsNotEmpty]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.IsNotEmpty}`),
      [RuleOperator.In]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.In}`),
      [RuleOperator.NotIn]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotIn}`),
      [RuleOperator.DateIsWithinNext]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsWithinNext}`),
      [RuleOperator.DateIsNotWithinNext]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsNotWithinNext}`),
      [RuleOperator.InASingleGeofence]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.InASingleGeofence}`),
      [RuleOperator.NotInASingleGeofence]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotInASingleGeofence}`),
      [RuleOperator.EqualNoCase]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.EqualNoCase}`),
      [RuleOperator.NotEqualNoCase]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotEqualNoCase}`)
    };

    this.dateFilterValues = [
      RuleOperator.DateIsWithinLast,
      RuleOperator.DateIsNotWithinLast,
      RuleOperator.DateIsWithinNext,
      RuleOperator.DateIsNotWithinNext,
      RuleOperator.DateIsGreaterThan,
      RuleOperator.DateIsLessThan
    ];

    this.store.select(fromUserProfile.selectDistanceUnit).subscribe(unit => {
      if (unit?.toLocaleLowerCase() === 'miles') {
        this.unitsConversionValue = 1609;
        this.distanceName = this.translate.instant('UNITS.DISTANCE.MILES-ABBREVIATED-LOWER');
      } else {
        this.unitsConversionValue = 1000;
        this.distanceName = this.translate.instant('UNITS.DISTANCE.KILOMETERS-ABBREVIATED-LOWER');
      }
    });

    if (!!this._actionIfDescription) {
      this._actionIfDescription = `${this._actionIfDescription} `;
    }
    if (!!this._actionResultDescription) {
      this._actionResultDescription = ` ${this._actionResultDescription}`;
    }

    this.andText = this.translate.instant('FILTERS.AND');
    this.orText = this.translate.instant('FILTERS.OR');

    this.buildFilterDisplayText();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showFilterClick(): void {
    if (this.readOnly === true) {
      return;
    }
    this._filterGroupValueBeforeEdit = this._filterGroup ? cloneDeep(this._filterGroup) : null;
    this.showFilterDialog = true;
  }

  newFilterClick(): void {
    if (this.readOnly === true) {
      return;
    }
    this._filterGroupValueBeforeEdit = this._filterGroup ? cloneDeep(this._filterGroup) : null;
    this.filterGroup = {
      uId: null,
      filters: [],
      filterGroups: [],
      groupType: FilterGroupType.And,
      fromDatasetUId: this.datasets[0]?.uId,
      toDatasetUId: this.datasets[1]?.uId
    };

    this.showFilterDialog = true;
  }

  filterCancel(): void {
    this._filterGroup = this._filterGroupValueBeforeEdit;
    this.showFilterDialog = false;
    this.buildFilterDisplayText();
  }

  onFilterChanged(): void {
    this.filterGroup = this.validateChangedFilter(this.filterGroup);
    this._filterGroupValueBeforeEdit = this._filterGroup;
    this.filterChanged.emit(this.filterGroup);
    this.showFilterDialog = false;
    this.buildFilterDisplayText();
  }

  validateChangedFilter(filterGroup: LinkFilterGroup): LinkFilterGroup {
    if (!filterGroup) {
      return null;
    }
    const hasFilters = filterGroup.filters?.length > 0;
    const hasGroups = filterGroup.filterGroups?.length > 0;
    if (!hasFilters && !hasGroups) {
      return null;
    }

    const validatedGroup = {
      ...filterGroup
    } as LinkFilterGroup;
    validatedGroup.filterGroups = filterGroup.filterGroups?.map(f => this.validateChangedFilter(f)).filter(g => g);
    validatedGroup.filters = filterGroup.filters
      ?.map(f => {
        if (f.operator > 0 && f.fromSystemField) {
          return f;
        }
        return null;
      })
      .filter(f => f);
    return validatedGroup;
  }

  buildFilterDisplayText(): void {
    if (!this.filterGroup || (this.filterGroup.filters.length === 0 && this.filterGroup.filterGroups.length === 0)) {
      this.showNewFilterButton = !this.readOnly;
      return;
    }

    this.showNewFilterButton = false;

    const builder = new FilterDisplayBuilder();
    this.processFilterGroup(this.filterGroup, builder);

    this.filterDisplayText = `${this._actionIfDescription}${builder.buildFilterDisplayHtml(this.maxLength)}${
      this._actionResultDescription
    }`;
  }

  processFilterGroup(filterGroup: LinkFilterGroup, builder: FilterDisplayBuilder): void {
    const dataset = this.datasets?.find(d => d.uId === filterGroup.fromDatasetUId);
    if (!dataset) {
      return;
    }

    for (let filter of filterGroup.filters) {
      const sourceField = dataset?.schema.fields.find(f => f.systemName === filter.fromSystemField);
      let fieldName = `${dataset.name} : ${sourceField?.displayName}`;
      const operator = this.operatorDisplayValues[filter.operator];

      let toDataset = null;
      let destField = null;
      if (!!filterGroup.toDatasetUId && !!filter.toSystemField) {
        toDataset = this.datasets?.find(d => d.uId === filterGroup.toDatasetUId);
        destField = toDataset?.schema.fields.find(f => f.systemName === filter.toSystemField);
      }

      if (!builder.isEmpty) {
        if (filterGroup.groupType === FilterGroupType.And) {
          builder.appendNormalText(this.andText);
        } else {
          builder.appendNormalText(this.orText);
        }
      }

      if (filter.operator === RuleOperator.IsEmpty || filter.operator === RuleOperator.IsNotEmpty) {
        builder.appendBoldText(fieldName);
        builder.appendNormalText(operator);
        continue;
      }

      if (filter.operator === RuleOperator.In || filter.operator === RuleOperator.NotIn) {
        builder.appendBoldText(fieldName);
        builder.appendNormalText(operator);
        filter.value
          ?.split('||')
          .filter(v => v?.length > 0)
          .map(v => builder.appendTag(v));
        continue;
      }

      if (filter.operator === RuleOperator.InASingleGeofence || filter.operator === RuleOperator.NotInASingleGeofence) {
        builder.appendBoldText(fieldName);
        builder.appendNormalText(operator);
        continue;
      }

      builder.appendBoldText(fieldName);
      builder.appendNormalText(operator);

      if (!!destField) {
        builder.appendBoldText(`${toDataset.name} : ${destField.displayName}`);
      }
      else {
        if (sourceField.dataType === FieldDataType.Picklist) {
          const picklistValue = sourceField.picklistValues.find(pl => pl.uId === filter.value);
          builder.appendBoldText(!!picklistValue ? picklistValue.value : '-');
        } else if (sourceField.dataType === FieldDataType.Distance) {
          let displayValue = 0;

          if (filter.value) {
            displayValue = filter.value / this.unitsConversionValue;
          }

          builder.appendBoldText(`${displayValue}${this.distanceName}`);
        } else if (sourceField.dataType === FieldDataType.UId) {
          // assume workflow
          const workflowStep = dataset?.workflow?.workflowSteps.find(ws => ws.uId === filter.value);
          builder.appendColoredText(workflowStep?.statusName, workflowStep?.statusHtmlColorCode);
        } else {
          builder.appendBoldText(filter.value);

          if (!!filter.options && this.dateFilterValues.includes(filter.operator)) {
            builder.appendBoldText(this.translate.instant(`FILTERS.FILTER-OPTIONS-${filter.options}`));
          }
        }
      }
    }

    for (let group of filterGroup.filterGroups) {
      if (!builder.isEmpty) {
        if (filterGroup.groupType === FilterGroupType.And) {
          builder.appendNormalText(this.andText);
        } else {
          builder.appendNormalText(this.orText);
        }
      }

      this.processFilterGroup(group, builder);
    }
  }
}
